import { IDictionaryMarket } from '@/creditflowresearch/domain/vo/IDictionary';
import Region from '@/creditflowresearch/domain/Region';
import Product from '@/creditflowresearch/domain/Product';
import { MarketName, MarketAbbreviations } from '@/creditflowresearch/domain/Market/types/MarketType';
import Segment from '@/creditflowresearch/domain/Segment';
import BaseDomainEntity from '@/creditflowresearch/domain/BaseDomainEntity';
import { MarketIdByName, MarketIdByAbbreviation, MarketAbbreviationById, MarketIdToNameMap, MarketAbbreviationToNameMap } from './constants';

abstract class Market extends BaseDomainEntity<Market, IDictionaryMarket> {
  static idRegistry = new Map<string, Market>();

  static getId(identifier: string): string | null {
    return MarketIdByName[identifier as MarketName]
      || MarketIdByAbbreviation[identifier as MarketAbbreviations]
      || null;
  }

  protected readonly productId: string | null;

  protected readonly displayName: MarketName;

  protected readonly name: MarketName;

  protected readonly priority: number;

  protected readonly abbreviation: MarketAbbreviations;

  constructor(marketData: IDictionaryMarket, abbreviation: MarketAbbreviations) {
    super(marketData);
    this.validateMarketData(marketData);
    this.displayName = marketData.name as MarketName;
    this.name = marketData.name as MarketName;
    this.priority = marketData.priority;
    this.abbreviation = abbreviation;
  }

  canAccess(accessibleMarketIds: string[] | Set<string>): boolean {
    if (accessibleMarketIds instanceof Set) {
      return accessibleMarketIds.has(this.id);
    }
    return accessibleMarketIds.includes(this.id);
  }

  getDisplayName(): string {
    return this.displayName;
  }

  getAbbreviation(): MarketAbbreviations {
    return this.abbreviation;
  }

  isOfType(type: MarketName): boolean {
    return this.name === type;
  }

  getProductId(): string | null {
    return this.productId;
  }

  getPriority(): number {
    return this.priority;
  }

  belongsToProduct(product: Product): boolean {
    if (!this.productId) {
      return false;
    }
    return product.getId() === this.productId;
  }

  abstract isBeta(): boolean;

  abstract isVisible(): boolean;

  abstract getSegments(): Segment[];

  getSegmentIds(): string[] {
    return this.getSegments().map((s) => s.getId());
  }

  belongsToRegion(): Region | null {
    const regionId = this.getRegionIdForMarket();
    if (!regionId) {
      return null;
    }

    return Region.from(regionId);
  }

  protected abstract getRegionIdForMarket(): string | null;

  private validateMarketData(marketData: IDictionaryMarket): void {
    if (!marketData) {
      throw new Error('Market data is required');
    }

    if (!marketData.id) {
      throw new Error('Market ID is required');
    }

    if (!marketData.name) {
      throw new Error('Market name is required');
    }

    if (typeof marketData.priority !== 'number') {
      throw new Error('Market priority is required');
    }
  }

  static getAbbreviation(productId: string): MarketAbbreviations | null {
    const abbreviation = MarketAbbreviationById[productId];

    if (!abbreviation) {
      return null;
    }

    return abbreviation as MarketAbbreviations;
  }

  static isOfType(identifier: string, type: MarketName): boolean {
    const typeById = MarketIdToNameMap[identifier];
    const typeByAbbreviation = MarketAbbreviationToNameMap[identifier];

    return typeById === type || typeByAbbreviation === type;
  }

  static areAllOfType(identifiers: string[], type: MarketName): boolean {
    if (!identifiers?.length || !type) {
      return false;
    }

    return identifiers.every((identifier) => Market.isOfType(identifier, type));
  }

  static areAllOfTypes(identifiers: string[], types: MarketName[]): boolean {
    if (!identifiers?.length || !types?.length) {
      return false;
    }

    return identifiers.every((identifier) =>
      types.some((type) => Market.isOfType(identifier, type)),
    );
  }

  static hasAnyOfType(identifiers: string[], type: MarketName): boolean {
    if (!identifiers?.length || !type) {
      return false;
    }

    return identifiers.some((identifier) => Market.isOfType(identifier, type));
  }

  static containAllTypes(identifiers: string[], types: MarketName[]): boolean {
    if (!identifiers?.length || !types?.length) {
      return false;
    }

    return types.every((type) => Market.hasAnyOfType(identifiers, type));
  }

  static getRegionsByMarkets = (identifiers: string[]) => {
    return identifiers.map((identifier) => {
      const market = Market.from(identifier);

      if (!market) {
        return null;
      }

      return market.belongsToRegion();
    }).filter(Boolean);
  };
}

export default Market;
